/**
 * Selfie Verification — native provider biometric flow.
 *
 * Mobile opens Didit's native SDK with a server-created session token. Web
 * keeps the hosted web flow. Only Didit's signed webhook / server decision
 * API can approve the step.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ActivityIndicator, Text, View, Platform, useWindowDimensions } from 'react-native'
import { useRouter } from 'expo-router'
import * as Linking from 'expo-linking'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import Animated, { FadeInDown, FadeOut, useReducedMotion } from 'react-native-reanimated'
import { AlertCircle, CheckCircle, ShieldCheck, ScanFace } from '@/components/ui/icons'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Button } from '@/components/ui/Button'
import { useAuth, authKeys } from '@/lib/hooks/use-auth'
import { useVerificationOverview, verificationKeys } from '@/lib/hooks/use-verification'
import { verificationService } from '@/lib/services/verification.service'
import type {
  LiveSelfieDecision,
  LiveSelfieSession,
  LiveSelfieStatus,
} from '@/lib/types/verification.types'
import {
  useSemanticColors,
  withAlpha,
  SPACING,
  RADIUS,
  DURATION,
  STAGGER,
} from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import { COLORS, FONTS } from '@/lib/brand'
import { notification, NotificationType, impact, ImpactStyle, selection } from '@/lib/utils/haptics'
import { captureError, errorMessage } from '@/lib/utils/observability'
import { hasDiditNativeSdk, loadDiditNativeSdk } from '@/lib/native/didit-native'
import { env } from '@/lib/env'
import { IOS_APP_STORE_URL } from '@/lib/constants/app-store'

let WebView: typeof import('react-native-webview').WebView | undefined
if (Platform.OS !== 'web') {
  try {
    const rnw = require('react-native-webview')
    WebView = rnw.WebView ?? rnw.default
  } catch {
    // Native WebView module unavailable in this build.
  }
}
type WebViewNavigation = import('react-native-webview').WebViewNavigation

type SelfiePhase = 'idle' | 'opening' | 'session' | 'verifying' | 'approved' | 'declined' | 'error'

const TERMINAL_STATUSES: readonly LiveSelfieStatus[] = [
  'approved',
  'declined',
  'abandoned',
  'expired',
]
const ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=app.tago.rent'

export default function SelfieScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { user } = useAuth()
  const reducedMotion = useReducedMotion()
  const { height } = useWindowDimensions()
  const { data: overview, error: overviewError } = useVerificationOverview()
  const selfieApproved = overview?.selfie.status === 'approved'

  const [phase, setPhase] = useState<SelfiePhase>(selfieApproved ? 'approved' : 'idle')
  const [session, setSession] = useState<LiveSelfieSession | null>(null)
  const [decision, setDecision] = useState<LiveSelfieDecision | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [progressIndex, setProgressIndex] = useState(0)
  const [requiresStoreUpdate, setRequiresStoreUpdate] = useState(false)
  const [webSessionOpened, setWebSessionOpened] = useState(false)

  useEffect(() => {
    if (overviewError && __DEV__) {
      console.error('[Selfie] verification overview query failed:', overviewError)
    }
  }, [overviewError])

  useEffect(() => {
    if (selfieApproved) {
      setRequiresStoreUpdate(false)
      setPhase('approved')
    }
  }, [selfieApproved])

  useEffect(() => {
    if (phase === 'approved') {
      const timer = setTimeout(() => router.back(), 1400)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [phase, router])

  useEffect(() => {
    if (phase !== 'opening' && phase !== 'session' && phase !== 'verifying') {
      setProgressIndex(0)
      return undefined
    }
    const interval = setInterval(() => {
      setProgressIndex((prev) => {
        const next = (prev + 1) % 4
        selection()
        return next
      })
    }, 1100)
    return () => clearInterval(interval)
  }, [phase])

  const callbackUrl = useMemo(() => {
    if (Platform.OS === 'web') return Linking.createURL('verification/selfie-callback')
    return undefined
  }, [])

  const refreshOverview = useCallback(async () => {
    if (!user?.id) return
    const queryKey = verificationKeys.overview(user.id)
    await queryClient.invalidateQueries({ queryKey })
    await queryClient.refetchQueries({ queryKey, type: 'active' })
    // The selfie row change drives profiles.is_verified server-side; converge
    // the profile badge/gates now instead of on the 10-minute staleTime.
    await queryClient.invalidateQueries({ queryKey: authKeys.profile(user.id) })
  }, [queryClient, user?.id])

  const refreshUntilTerminal = useCallback(
    async (liveSession: LiveSelfieSession, providerSessionId?: string | null) => {
      const request = {
        sessionId: liveSession.sessionId,
        providerSessionId: providerSessionId ?? liveSession.providerSessionId,
      }

      for (let attempt = 0; attempt < 12; attempt += 1) {
        const refreshed = await verificationService.refreshLiveSelfieSession(
          user?.id ?? '',
          request,
        )
        setDecision(refreshed)
        if (TERMINAL_STATUSES.includes(refreshed.status)) return refreshed
        await delay(1400 + attempt * 350)
      }

      return verificationService.refreshLiveSelfieSession(user?.id ?? '', request)
    },
    [user?.id],
  )

  const finishSession = useCallback(
    async (liveSession: LiveSelfieSession, providerSessionId?: string | null) => {
      setPhase('verifying')
      try {
        const finalDecision = await refreshUntilTerminal(liveSession, providerSessionId)

        if (finalDecision.status === 'approved') {
          await refreshOverview()
          void notification(NotificationType.Success)
          setPhase('approved')
        } else if (finalDecision.status === 'declined') {
          await refreshOverview()
          void notification(NotificationType.Warning)
          setError(finalDecision.rejectionReason)
          setPhase('declined')
        } else {
          void notification(NotificationType.Warning)
          setError(t('verification.selfie_still_processing'))
          setPhase('error')
        }
      } catch (err) {
        if (__DEV__) console.error('[Selfie live refresh]', err)
        void notification(NotificationType.Error)
        setError(errorMessage(err, t('verification.selfie_session_failed')))
        setPhase('error')
        captureError(err, { screen: 'verification/selfie', action: 'refresh-live-session' })
      }
    },
    [refreshOverview, refreshUntilTerminal, t],
  )

  const startNativeVerification = useCallback(
    async (liveSession: LiveSelfieSession) => {
      if (!liveSession.sessionToken) {
        throw new Error('Selfie session token missing')
      }

      const { startVerification } = await loadDiditNativeSdk()
      const result = await startVerification(liveSession.sessionToken, {
        languageCode: 'es',
        loggingEnabled: __DEV__,
        showCloseButton: true,
        showExitConfirmation: true,
        closeOnComplete: false,
      })

      if (result.type === 'completed') {
        await finishSession(liveSession)
        return
      }

      if (result.type === 'cancelled') {
        setSession(liveSession)
        setPhase('idle')
        return
      }

      throw new Error(result.error?.message ?? 'Didit verification did not complete')
    },
    [finishSession],
  )

  const handleStart = useCallback(async () => {
    if (!user?.id) return
    impact(ImpactStyle.Medium)
    setError(null)
    setDecision(null)
    setRequiresStoreUpdate(false)
    setWebSessionOpened(false)

    if (Platform.OS !== 'web' && !hasDiditNativeSdk()) {
      setRequiresStoreUpdate(true)
      setError(t('updates.native_capability_required_body'))
      setPhase('error')
      captureError(new Error('DIDIT_NATIVE_MODULE_MISSING'), {
        screen: 'verification/selfie',
        action: 'native-capability-check',
      })
      return
    }

    setPhase('opening')

    try {
      const liveSession = await verificationService.startLiveSelfieSession(user.id, {
        callbackUrl,
        native: Platform.OS !== 'web',
      })
      setSession(liveSession)

      if (liveSession.status === 'approved') {
        await refreshOverview()
        void notification(NotificationType.Success)
        setPhase('approved')
        return
      }
      if (Platform.OS !== 'web') {
        await startNativeVerification(liveSession)
        return
      }

      if (!liveSession.url) {
        throw new Error('Selfie session URL missing')
      }

      setPhase('session')
    } catch (err) {
      if (__DEV__) console.error('[Selfie live verification]', err)
      void notification(NotificationType.Error)
      setError(errorMessage(err, t('verification.selfie_session_failed')))
      setPhase('error')
      captureError(err, { screen: 'verification/selfie', action: 'live-session' })
    }
  }, [callbackUrl, refreshOverview, startNativeVerification, t, user?.id])

  const handleOpenWebSession = useCallback(() => {
    if (!session?.url) return
    setWebSessionOpened(true)
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const opened = window.open(session.url, '_blank', 'noopener,noreferrer')
      if (opened) return
    }
    void Linking.openURL(session.url)
  }, [session?.url])

  const handleCheckWebSession = useCallback(() => {
    if (!session) return
    void finishSession(session)
  }, [finishSession, session])

  const handleWebViewRequest = useCallback(
    (request: WebViewNavigation) => {
      if (isCompletionUrl(request.url)) {
        if (session) void finishSession(session, providerSessionIdFromUrl(request.url))
        return false
      }
      return isAllowedDiditNavigation(request.url)
    },
    [finishSession, session],
  )

  const handleWebViewNavigation = useCallback(
    (navState: WebViewNavigation) => {
      if (isCompletionUrl(navState.url) && session) {
        void finishSession(session, providerSessionIdFromUrl(navState.url))
      }
    },
    [finishSession, session],
  )

  const nativeStoreUrl =
    Platform.OS === 'ios'
      ? (env.EXPO_PUBLIC_IOS_APP_STORE_URL ?? IOS_APP_STORE_URL)
      : ANDROID_STORE_URL

  const handleRetry = useCallback(() => {
    if (requiresStoreUpdate) {
      if (nativeStoreUrl) {
        void Linking.openURL(nativeStoreUrl)
      }
      return
    }
    setError(null)
    setDecision(null)
    setSession(null)
    setWebSessionOpened(false)
    setPhase('idle')
  }, [nativeStoreUrl, requiresStoreUpdate])

  const progressKeys = [
    'verification.selfie_live_progress_starting',
    'verification.selfie_live_progress_provider',
    'verification.selfie_live_progress_security',
    'verification.selfie_live_progress_saving',
  ] as const
  const progressLabel = t(progressKeys[progressIndex] ?? progressKeys[0])
  const overviewLoading = !overview && !overviewError
  const webViewHeight = Math.min(620, Math.max(420, Math.round(height * 0.62)))
  const startDisabled =
    overviewLoading || phase === 'opening' || phase === 'session' || phase === 'verifying'

  return (
    <ScreenContainer variant="reading">
      <ScreenHeader title={t('verification.selfie_title')} backTestID="selfie.back" />
      <View
        style={{
          flex: 1,
          paddingHorizontal: SPACING.lg,
          paddingTop: SPACING.sm,
          gap: SPACING.lg,
        }}
      >
        <Animated.View
          entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
          collapsable={false}
          style={{
            backgroundColor: colors.surface,
            borderRadius: RADIUS.lg,
            borderWidth: 1,
            borderColor: colors.border,
            padding: SPACING.lg,
            gap: SPACING.md,
          }}
        >
          <View
            style={{
              width: 72,
              height: 72,
              borderRadius: 36,
              alignItems: 'center',
              justifyContent: 'center',
              // Brand purple is almost the value of the dark card, so an 8%
              // tint of it plus a purple glyph collapsed into a smudge. In
              // dark the chip takes the light end of the ramp and the glyph
              // the theme's brand-as-text tint.
              backgroundColor: isDark
                ? withAlpha(COLORS.purple.light, 0.24)
                : `${COLORS.purple.DEFAULT}14`,
              alignSelf: 'center',
            }}
          >
            <ScanFace
              size={38}
              color={isDark ? colors.textBrand : COLORS.purple.DEFAULT}
              strokeWidth={1.6}
            />
          </View>
          <Text
            style={{
              fontSize: 17,
              lineHeight: 23,
              fontFamily: FONTS.bodyBold,
              color: colors.textPrimary,
              textAlign: 'center',
            }}
          >
            {t('verification.selfie_live_title')}
          </Text>
          <Text
            style={{
              fontSize: 14,
              lineHeight: 20,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
              textAlign: 'center',
            }}
          >
            {t('verification.selfie_live_body')}
          </Text>
        </Animated.View>

        {(phase === 'idle' || phase === 'opening') && (
          <Animated.View
            entering={reducedMotion ? undefined : FadeInDown.delay(STAGGER).duration(DURATION.fast)}
            collapsable={false}
            style={{
              gap: SPACING.md,
              backgroundColor: colors.surface,
              borderRadius: RADIUS.lg,
              borderWidth: 1,
              borderColor: colors.border,
              padding: SPACING.lg,
            }}
          >
            {/* Top-aligned: centring the shield on the whole text block landed
                it beside the first line of the body copy instead of the title
                it belongs to. */}
            <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: SPACING.md }}>
              <ShieldCheck size={24} color={colors.success} />
              <View style={{ flex: 1, gap: SPACING.xs }}>
                <Text
                  style={{ fontSize: 15, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}
                >
                  {t('verification.selfie_live_secure_title')}
                </Text>
                <Text
                  style={{
                    fontSize: 13,
                    lineHeight: 19,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                  }}
                >
                  {t('verification.selfie_live_secure_body')}
                </Text>
              </View>
            </View>

            {phase === 'opening' ? (
              <View
                style={{
                  borderRadius: RADIUS.md,
                  backgroundColor: `${COLORS.purple.DEFAULT}12`,
                  paddingVertical: SPACING.md,
                  paddingHorizontal: SPACING.md,
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: SPACING.md,
                }}
              >
                <ActivityIndicator color={COLORS.purple.DEFAULT} />
                <Text
                  style={{
                    flex: 1,
                    fontSize: 14,
                    fontFamily: FONTS.body,
                    color: colors.textPrimary,
                  }}
                >
                  {progressLabel}
                </Text>
              </View>
            ) : null}
          </Animated.View>
        )}

        {phase === 'session' && session?.url ? (
          Platform.OS === 'web' ? (
            <Animated.View
              entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
              collapsable={false}
              style={{
                gap: SPACING.md,
                borderRadius: RADIUS.lg,
                borderWidth: 1,
                borderColor: colors.border,
                backgroundColor: colors.surface,
                padding: SPACING.lg,
              }}
              testID="selfie.web-session-card"
            >
              <View style={{ gap: SPACING.xs }}>
                <Text
                  style={{
                    fontSize: 16,
                    lineHeight: 22,
                    fontFamily: FONTS.bodyBold,
                    color: colors.textPrimary,
                  }}
                >
                  {t('verification.selfie_web_title')}
                </Text>
                <Text
                  style={{
                    fontSize: 14,
                    lineHeight: 20,
                    fontFamily: FONTS.body,
                    color: colors.textSecondary,
                  }}
                >
                  {t('verification.selfie_web_body')}
                </Text>
              </View>
              <Button
                title={t('verification.selfie_web_open')}
                onPress={handleOpenWebSession}
                variant="primary"
                size="lg"
                testID="selfie.open-web-session"
              />
              <Button
                title={t('verification.selfie_web_check')}
                onPress={handleCheckWebSession}
                variant="secondary"
                size="lg"
                disabled={!webSessionOpened}
                testID="selfie.check-web-session"
              />
            </Animated.View>
          ) : WebView ? (
            <Animated.View
              entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
              collapsable={false}
              style={{
                height: webViewHeight,
                borderRadius: RADIUS.lg,
                borderWidth: 1,
                borderColor: colors.border,
                overflow: 'hidden',
                backgroundColor: colors.surface,
              }}
            >
              <WebView
                source={{ uri: session.url }}
                originWhitelist={[
                  'https://verify.didit.me',
                  'https://verification.didit.me',
                  'https://*.didit.me',
                  'tago://*',
                  'https://app.tago.rent',
                ]}
                onShouldStartLoadWithRequest={handleWebViewRequest}
                onNavigationStateChange={handleWebViewNavigation}
                onError={(event) => {
                  if (isCompletionUrl(event.nativeEvent.url)) return
                  setError(event.nativeEvent.description)
                  setPhase('error')
                }}
                onHttpError={(event) => {
                  if (event.nativeEvent.statusCode >= 400) {
                    setError(t('verification.selfie_session_failed'))
                    setPhase('error')
                  }
                }}
                startInLoadingState
                renderLoading={() => (
                  <View
                    style={{
                      flex: 1,
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: SPACING.md,
                      backgroundColor: colors.surface,
                    }}
                  >
                    <ActivityIndicator color={COLORS.purple.DEFAULT} />
                    <Text
                      style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textSecondary }}
                    >
                      {t('verification.selfie_live_progress_provider')}
                    </Text>
                  </View>
                )}
                javaScriptEnabled
                domStorageEnabled
                allowsInlineMediaPlayback
                mediaPlaybackRequiresUserAction={false}
                mediaCapturePermissionGrantType="grantIfSameHostElsePrompt"
                setSupportMultipleWindows={false}
                javaScriptCanOpenWindowsAutomatically={false}
                thirdPartyCookiesEnabled={false}
                sharedCookiesEnabled={false}
                incognito
                cacheEnabled={false}
                mixedContentMode="never"
                allowsLinkPreview={false}
                fraudulentWebsiteWarningEnabled
                style={{ flex: 1, backgroundColor: colors.surface }}
                testID="selfie.didit-webview"
              />
            </Animated.View>
          ) : null
        ) : null}

        {phase === 'verifying' && (
          <Animated.View
            entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
            collapsable={false}
            style={{
              gap: SPACING.md,
              backgroundColor: colors.surface,
              borderRadius: RADIUS.lg,
              borderWidth: 1,
              borderColor: colors.border,
              padding: SPACING.lg,
            }}
          >
            <View
              style={{
                borderRadius: RADIUS.md,
                backgroundColor: `${COLORS.purple.DEFAULT}12`,
                paddingVertical: SPACING.md,
                paddingHorizontal: SPACING.md,
                flexDirection: 'row',
                alignItems: 'center',
                gap: SPACING.md,
              }}
            >
              <ActivityIndicator color={COLORS.purple.DEFAULT} />
              <Text
                style={{ flex: 1, fontSize: 14, fontFamily: FONTS.body, color: colors.textPrimary }}
              >
                {progressLabel}
              </Text>
            </View>
            <Text
              style={{
                fontSize: 13,
                lineHeight: 19,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
              }}
            >
              {t('verification.selfie_verifying_helper')}
            </Text>
          </Animated.View>
        )}

        {phase === 'approved' && (
          <ResultCard
            tone="success"
            icon={<CheckCircle size={26} color={colors.success} />}
            title={t('verification.selfie_result_approved')}
            body={t('verification.selfie_live_approved_body')}
            decision={decision}
          />
        )}

        {(phase === 'declined' || phase === 'error') && (
          <Animated.View
            entering={reducedMotion ? undefined : FadeInDown.duration(DURATION.fast)}
            exiting={reducedMotion ? undefined : FadeOut.duration(DURATION.fast)}
            collapsable={false}
            style={{ gap: SPACING.md }}
          >
            <ResultCard
              tone="error"
              icon={<AlertCircle size={26} color={colors.textError} />}
              title={
                phase === 'declined'
                  ? t('verification.result_declined_title')
                  : t('verification.error_failed')
              }
              body={error ?? t('verification.selfie_session_failed')}
              decision={decision}
            />
            <Button
              title={requiresStoreUpdate ? t('updates.open_store') : t('verification.try_again')}
              onPress={handleRetry}
              variant="primary"
              size="lg"
              testID="selfie.retry-button"
            />
          </Animated.View>
        )}

        {/* The screen's primary action. It used to live inside the "Sesión
            protegida" card, directly under a two-line security note, so the
            one thing the user came here to do read as a footnote's
            sub-element — with ~340pt of empty background below it. The spacer
            drops it onto the bottom safe area and collapses when the content
            is tall enough to scroll. */}
        {(phase === 'idle' || phase === 'opening') && (
          <>
            <View style={{ flex: 1 }} />
            <Button
              title={phase === 'idle' ? t('verification.selfie_live_start') : progressLabel}
              onPress={handleStart}
              loading={phase === 'opening' || overviewLoading}
              disabled={startDisabled}
              variant="primary"
              size="lg"
              testID="selfie.start-live-button"
            />
          </>
        )}
      </View>
    </ScreenContainer>
  )
}

function ResultCard({
  tone,
  icon,
  title,
  body,
  decision,
}: {
  tone: 'success' | 'error'
  icon: React.ReactNode
  title: string
  body: string
  decision: LiveSelfieDecision | null
}) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const toneColor = tone === 'success' ? colors.success : colors.error
  const livenessScore = decision?.livenessScore ?? null
  const faceMatchScore = decision?.faceMatchScore ?? null
  return (
    <Animated.View
      entering={FadeInDown.duration(DURATION.fast)}
      collapsable={false}
      style={{
        gap: SPACING.md,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: toneColor,
        backgroundColor: `${toneColor}12`,
        padding: SPACING.lg,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: SPACING.md }}>
        {icon}
        <View style={{ flex: 1, gap: SPACING.xs }}>
          <Text style={{ fontSize: 16, fontFamily: FONTS.bodyBold, color: toneColor }}>
            {title}
          </Text>
          <Text
            style={{
              fontSize: 14,
              lineHeight: 20,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
            }}
          >
            {body}
          </Text>
        </View>
      </View>
      {livenessScore !== null || faceMatchScore !== null ? (
        <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
          {livenessScore !== null ? (
            <ScorePill labelKey="verification.liveness_label" value={livenessScore} />
          ) : null}
          {faceMatchScore !== null ? (
            <ScorePill labelKey="verification.face_match_label" value={faceMatchScore} />
          ) : null}
        </View>
      ) : null}
    </Animated.View>
  )
}

function ScorePill({ labelKey, value }: { labelKey: string; value: number }) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <View
      style={{
        flex: 1,
        borderRadius: RADIUS.md,
        backgroundColor: colors.surface,
        padding: SPACING.md,
        gap: SPACING.xs,
      }}
    >
      <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textMuted }}>
        {t(labelKey)}
      </Text>
      <Text style={{ fontSize: 18, fontFamily: FONTS.bodyBold, color: colors.textPrimary }}>
        {Math.round(value)}%
      </Text>
    </View>
  )
}

function providerSessionIdFromUrl(url: string): string | null {
  try {
    const parsed = new URL(url)
    return parsed.searchParams.get('verificationSessionId')
  } catch {
    return null
  }
}

function isCompletionUrl(url: string): boolean {
  try {
    const parsed = new URL(url)
    return (
      (parsed.protocol === 'tago:' &&
        parsed.hostname === 'verification' &&
        parsed.pathname === '/selfie-callback' &&
        parsed.searchParams.has('verificationSessionId')) ||
      (parsed.protocol === 'https:' &&
        parsed.hostname === 'app.tago.rent' &&
        parsed.pathname === '/verification/selfie-callback' &&
        parsed.searchParams.has('verificationSessionId'))
    )
  } catch {
    return false
  }
}

function isAllowedDiditNavigation(url: string): boolean {
  if (url === 'about:blank') return true
  if (url.startsWith('tago://verification/selfie-callback')) return false
  try {
    const parsed = new URL(url)
    return (
      parsed.protocol === 'https:' &&
      (parsed.hostname === 'verify.didit.me' ||
        parsed.hostname === 'verification.didit.me' ||
        parsed.hostname.endsWith('.didit.me'))
    )
  } catch {
    return false
  }
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
